import { Link } from 'react-router-dom';
import { Hourglass } from 'lucide-react';
import { buttonVariants } from '../../components/ui';
import { formatDate, formatHours } from '../../lib/format';
import { LineBankAction } from '../invoices/LineBankAction';
import type { UnbankedSale } from './api';

/**
 * HOURS SOLD ON AN INVOICE THAT NEVER REACHED THE BANK — said beside the
 * balance, naming the invoice, with the repair one click away.
 *
 * Jesse, 2026-10-30, on Compulink reading 3.5 h over while #26-0003 had sold
 * 80 h: *"it's hard to tell that it's related to the invoice."* The repair
 * ("Add N h to the bank") lived only on the invoice line; nothing on the bank
 * said an invoice was involved. The button here is the SAME `LineBankAction`
 * the invoice line renders — one repair, two doors.
 */
export function UnbankedSales({
  clientId,
  sales,
  overage,
}: {
  clientId: string;
  sales: UnbankedSale[];
  /** The bank's current overage — when it exists, the sale is the likely cause. */
  overage: number;
}) {
  if (sales.length === 0) return null;
  const total = sales.reduce((sum, s) => sum + s.hours, 0);
  return (
    <div className="mt-4 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-body-sm text-secondary">
      <p className="flex items-start gap-1.5">
        <Hourglass className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
        <span>
          <span className="num font-semibold">
            {formatHours(total, { unit: true })} sold on{' '}
            {sales.length === 1 ? `invoice #${sales[0].invoiceNumber}` : `${sales.length} invoice lines`} never
            reached this bank.
          </span>{' '}
          {overage > 0
            ? total >= overage
              ? `That is why it reads ${formatHours(overage, { unit: true })} over — add them and the overage clears. Don't invoice the overage first: those hours are already billed.`
              : `It explains part of the ${formatHours(overage, { unit: true })} over — add them first, then invoice whatever is left.`
            : 'Add them so the balance counts what was bought.'}
        </span>
      </p>
      <ul className="mt-2 space-y-2">
        {sales.map((s) => (
          <li key={s.lineId} className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span className="num min-w-0">
              <Link to={`/invoices/${s.invoiceId}`} className="font-semibold text-primary hover:underline">
                #{s.invoiceNumber}
              </Link>{' '}
              · {formatDate(s.issueDate)} · {s.description} · {formatHours(s.hours, { unit: true })}
              {s.invoiceState === 'DRAFT' ? ' · draft, not sent' : ''}
            </span>
            <span className="flex flex-wrap items-center gap-2">
              <LineBankAction
                invoiceId={s.invoiceId}
                invoiceNumber={s.invoiceNumber}
                invoiceSent={s.invoiceState !== 'DRAFT'}
                clientId={clientId}
                line={{
                  id: s.lineId,
                  agreement: s.agreement,
                  quantity: s.hours,
                  description: s.description,
                  prepaid: { bankedHours: null, canAdd: true },
                }}
                asButton
              />
              <Link to={`/invoices/${s.invoiceId}`} className={buttonVariants({ size: 'sm', variant: 'outline' })}>
                Open #{s.invoiceNumber}
              </Link>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
